import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import createPersistedState from 'vuex-persistedstate'
import api from '../service/api'
import msalInstance from '../auth/msalInstance'
import { loginRequest } from '../auth/msalConfig'

Vue.use(Vuex)

export default new Vuex.Store({
  plugins: [createPersistedState({ paths: ['selectedRegatta'] })],

  state: {
    // Auth
    account: null,
    idToken: null,
    userRole: null,
    userName: null,

    // Rowing data
    selectedRegatta: null,
    regattas: [],
    events: [],
    races: [],
    participantResults: [],
    cachedResultIds: [],
    allAthleteResults: [],
    athleteResultsLoading: false,
    loading: false,
    resultsLoading: false,
    notification: null
  },

  getters: {
    // Auth
    isAuthenticated: (state) => !!state.account,
    idToken: (state) => state.idToken,
    userRole: (state) => state.userRole,
    userName: (state) => state.userName,
    isAdmin: (state) => state.userRole === 'admin',

    // Rowing
    racesForEvent: (state) => (eventCode) => {
      if (!eventCode) return state.races
      return state.races.filter(r => r.eventCode === eventCode)
    },

    resultsByEvent: (state) => (eventCode) => {
      const rows = eventCode
        ? state.participantResults.filter(r => r.eventCode === eventCode)
        : state.participantResults
      const crews = {}
      rows.forEach(r => {
        const key = `${r.raceNumber}__${r.crewTitle}`
        if (!crews[key]) {
          crews[key] = {
            raceNumber: r.raceNumber,
            eventCode: r.eventCode,
            crewTitle: r.crewTitle,
            raceLane: r.raceLane,
            resultPlace: r.resultPlace,
            resultFinish: r.resultFinish,
            raceStatus: r.raceStatus,
            athletes: []
          }
        }
        crews[key].athletes.push({ name: r.athleteName, club: r.athleteClub })
      })
      return Object.values(crews).sort((a, b) => {
        const pa = parsePlaceNum(a.resultPlace)
        const pb = parsePlaceNum(b.resultPlace)
        if (pa !== pb) return pa - pb
        return (a.raceNumber || '').localeCompare(b.raceNumber || '')
      })
    },

    laneStats: (state) => {
      if (state.participantResults.length) {
        const stats = {}
        state.participantResults.forEach(r => {
          const lane = r.raceLane
          const place = parsePlaceNum(r.resultPlace)
          if (!lane || !place) return
          if (!stats[lane]) stats[lane] = { lane, races: 0, wins: 0, podiums: 0, totalPlace: 0 }
          stats[lane].races++
          stats[lane].totalPlace += place
          if (place === 1) stats[lane].wins++
          if (place <= 3) stats[lane].podiums++
        })
        return Object.values(stats)
          .map(s => ({
            ...s,
            winPct: s.races ? ((s.wins / s.races) * 100).toFixed(1) : 0,
            podiumPct: s.races ? ((s.podiums / s.races) * 100).toFixed(1) : 0,
            avgPlace: s.races ? (s.totalPlace / s.races).toFixed(2) : 0
          }))
          .sort((a, b) => a.lane - b.lane)
      }
      const stats = {}
      state.races.forEach(race => {
        race.lanes.forEach(lane => {
          if (!lane.laneNum || !lane.place) return
          if (!stats[lane.laneNum]) stats[lane.laneNum] = { lane: lane.laneNum, races: 0, wins: 0, podiums: 0, totalPlace: 0 }
          stats[lane.laneNum].races++
          stats[lane.laneNum].totalPlace += lane.place
          if (lane.place === 1) stats[lane.laneNum].wins++
          if (lane.place <= 3) stats[lane.laneNum].podiums++
        })
      })
      return Object.values(stats)
        .map(s => ({
          ...s,
          winPct: s.races ? ((s.wins / s.races) * 100).toFixed(1) : 0,
          podiumPct: s.races ? ((s.podiums / s.races) * 100).toFixed(1) : 0,
          avgPlace: s.races ? (s.totalPlace / s.races).toFixed(2) : 0
        }))
        .sort((a, b) => a.lane - b.lane)
    }
  },

  mutations: {
    // Auth
    SET_ACCOUNT(state, { account, idToken }) {
      state.account = account
      state.idToken = idToken
    },
    SET_ID_TOKEN(state, token) { state.idToken = token },
    SET_USER_PROFILE(state, { role, displayName }) {
      state.userRole = role
      state.userName = displayName
    },
    CLEAR_AUTH(state) {
      state.account = null
      state.idToken = null
      state.userRole = null
      state.userName = null
    },

    // Rowing
    SET_SELECTED_REGATTA(state, regatta) { state.selectedRegatta = regatta },
    SET_REGATTAS(state, list) { state.regattas = list },
    SET_EVENTS(state, list) { state.events = list },
    SET_RACES(state, list) { state.races = list },
    SET_PARTICIPANT_RESULTS(state, list) { state.participantResults = list },
    SET_CACHED_RESULT_IDS(state, list) { state.cachedResultIds = list },
    SET_ALL_ATHLETE_RESULTS(state, list) { state.allAthleteResults = list },
    SET_ATHLETE_RESULTS_LOADING(state, val) { state.athleteResultsLoading = val },
    SET_LOADING(state, val) { state.loading = val },
    SET_RESULTS_LOADING(state, val) { state.resultsLoading = val },
    SET_NOTIFICATION(state, n) { state.notification = n }
  },

  actions: {
    // ── Auth ──────────────────────────────────────────────────────────────────

    async loginSuccess({ commit, dispatch }, authResult) {
      commit('SET_ACCOUNT', { account: authResult.account, idToken: authResult.idToken })
      await dispatch('fetchUserProfile', authResult.idToken)
    },

    async fetchUserProfile({ commit, dispatch }, idToken) {
      try {
        const { data } = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${idToken}` }
        })
        commit('SET_USER_PROFILE', { role: data.role, displayName: data.display_name })
      } catch (error) {
        console.error('fetchUserProfile failed:', error?.response?.status, error?.response?.data)
        dispatch('logOut')
      }
    },

    initiateLogin() {
      msalInstance.loginRedirect(loginRequest)
    },

    logOut({ commit }) {
      const account = msalInstance.getAllAccounts()[0]
      commit('CLEAR_AUTH')
      msalInstance.logoutRedirect({ account })
    },

    // ── Rowing ────────────────────────────────────────────────────────────────

    async fetchRegattas({ commit }, params = { state: 'WA' }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.getRegattas(params)
        commit('SET_REGATTAS', Array.isArray(data) ? data : [])
      } catch (e) {
        commit('SET_NOTIFICATION', { text: 'Failed to load regattas', color: 'error', icon: 'mdi-alert-circle' })
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchEvents({ commit }, regattaId) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.getEvents(regattaId)
        commit('SET_EVENTS', Array.isArray(data) ? data : [])
      } catch (e) {
        commit('SET_EVENTS', [])
        if (e.response?.status !== 404) {
          commit('SET_NOTIFICATION', { text: 'Failed to load events', color: 'error', icon: 'mdi-alert-circle' })
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchRaces({ commit }, { regattaId, params = {} }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.getRaces(regattaId, params)
        commit('SET_RACES', Array.isArray(data) ? data : [])
      } catch (e) {
        commit('SET_RACES', [])
        if (e.response?.status !== 404) {
          commit('SET_NOTIFICATION', { text: 'Failed to load races', color: 'error', icon: 'mdi-alert-circle' })
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchParticipantResults({ commit }, regattaId) {
      commit('SET_RESULTS_LOADING', true)
      commit('SET_PARTICIPANT_RESULTS', [])
      try {
        const { data } = await api.getResults(regattaId)
        commit('SET_PARTICIPANT_RESULTS', Array.isArray(data) ? data : [])
      } catch (e) {
        const msg = e.response?.status === 409
          ? 'Participant results only available after hours (after 22:00 AEDT)'
          : 'Failed to load participant results'
        commit('SET_NOTIFICATION', { text: msg, color: 'warning', icon: 'mdi-clock-outline' })
      } finally {
        commit('SET_RESULTS_LOADING', false)
      }
    },

    async fetchAllCachedResults({ commit }) {
      commit('SET_ATHLETE_RESULTS_LOADING', true)
      try {
        const thisYear = new Date().getFullYear()
        const cutoff = new Date()
        cutoff.setFullYear(cutoff.getFullYear() - 1)

        const [cacheRes, waThis, waLast] = await Promise.allSettled([
          api.getCacheList(),
          api.getRegattas({ state: 'WA' }),
          api.getRegattas({ state: 'WA', year: thisYear - 1 })
        ])

        const cached = cacheRes.status === 'fulfilled' ? cacheRes.value.data : []
        const regattaLookup = {}
        for (const res of [waThis, waLast]) {
          if (res.status === 'fulfilled' && Array.isArray(res.value.data)) {
            res.value.data.forEach(r => { regattaLookup[r.regattaId] = r.name })
          }
        }

        const results = await Promise.all(
          cached.map(async ({ regattaId, fetchedAt }) => {
            try {
              const { data } = await api.getResults(regattaId)
              const rows = Array.isArray(data) ? data : []
              if (!rows.length) return []

              // Fetch name for regattas not in the WA list
              if (!regattaLookup[regattaId]) {
                try {
                  const { data: r } = await api.getRegatta(regattaId)
                  regattaLookup[regattaId] = r?.name || `Regatta ${regattaId}`
                } catch {
                  regattaLookup[regattaId] = `Regatta ${regattaId}`
                }
              }

              const regattaName = regattaLookup[regattaId]
              const regattaDate = rows[0]?.raceTime ? rows[0].raceTime.split(' ')[0]
                : fetchedAt ? fetchedAt.split(' ')[0] : null
              if (!regattaDate || new Date(regattaDate) < cutoff) return []

              return rows.map(r => ({ ...r, regattaName, regattaDate }))
            } catch { return [] }
          })
        )
        commit('SET_ALL_ATHLETE_RESULTS', results.flat())
      } catch {
        // silent — table will show empty
      } finally {
        commit('SET_ATHLETE_RESULTS_LOADING', false)
      }
    },

    selectRegatta({ commit, dispatch }, regatta) {
      commit('SET_SELECTED_REGATTA', regatta)
      commit('SET_EVENTS', [])
      commit('SET_RACES', [])
      commit('SET_PARTICIPANT_RESULTS', [])
      if (regatta) {
        dispatch('fetchEvents', regatta.regattaId)
        dispatch('fetchRaces', { regattaId: regatta.regattaId, params: { lanedraw: 'true' } })
      }
    }
  }
})

function parsePlaceNum(place) {
  if (!place || place === '-') return null
  return parseInt(place)
}
